// EmployeeAttendancePage.tsx

import { useEffect, useState } from "react";
import AttendanceCalendar from "./AttendanceCalendar";
import MonthPicker from "../../../../common/date-picker/MonthPicker";
import Button from "../../../../common/button/Button";
import PersonInfo from "../../../../common/person-info";
import Tabs, { TabOption } from "../../../../common/tabs/Tabs";
import { useNavigate, useParams } from "react-router-dom";
import { getUserAttendanceByUserId, getUserAttendanceSummary, UserAttendanceSummary } from "../../../../../apis/performance/attendance.api";
import {
  AttendanceViewEnum,
  RoleEnum,
  statusEnum,
} from "../../../../../types/common-types";
import EmployeeAttendanceTable from "./EmploeeAttendanceTable";
import PageLoader from "../../../../common/loader/PageLoader";
import { getEmployeeDetails } from "../../../../../apis/workforce/onboardings.api";
import { IUserAttendance } from "..";
import { pathNames, roleNames } from "../../../../../constants/constants";
import { IUser } from "../../../../../types/user.types";
import useDevice from "../../../../../hooks/useDevice";
import Modal from "../../../../common/modal/Modal";
import AttendanceSummary from "../../../../common/attendance-summary";

interface EmployeeAttendanceDetailsProps {
  id?: string;
}

const EmployeeAttendanceDetails = ({ id }: EmployeeAttendanceDetailsProps) => {
  const {isMobile} = useDevice();
  const navigate = useNavigate();
  const params = useParams();
  const userId: string = params.id ?? id ?? "";

  const [loading, setLoading] = useState<boolean>(true);
  // attendance summary states
  const [overview, setOverview] = useState<boolean>(false);

  // define tab option Calendar view and Table View
  const options: TabOption[] = [
    {
      label: AttendanceViewEnum.CALENDER_VIEW,
      icon: <i className="fa-regular fa-calendar-check text-[24px] sm:text-md"></i>,
      activeColor: "bg-primaryPurple",
      bgColor: "bg-white",
    },
    {
      label: AttendanceViewEnum.TABLE_VIEW,
      icon: <i className="fa-solid fa-table-cells-large text-[24px] sm:text-md"></i>,
      activeColor: "bg-primaryPurple",
      bgColor: "bg-white",
    },
  ];

  // active tab state
  const [active, setActive] = useState<AttendanceViewEnum>(
    isMobile ? AttendanceViewEnum.TABLE_VIEW : AttendanceViewEnum.CALENDER_VIEW,
  );

  // employee attendance details state
  const [attendanceDetails, setAttendanceDetails] = useState<IUserAttendance[]>(
    [],
  );
  const [attendanceSummary, setAttendanceSummary] =
    useState<UserAttendanceSummary | null>(null);

  // employee details state
  const initialState: IUser = {
    _id: "",
    firstName: "",
    lastName: "",
    profileImage: "",
    status: statusEnum.ACTIVE,
    role: RoleEnum.EMPLOYEE,
    userId:""
  };
  const [employee, setEmployee] = useState<IUser>(initialState);

  const [selectedMonth, setSelectedMonth] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  useEffect(() => {
    if (userId) {
      fetchUserAttendance();
    }
    // eslint-disable-next-line
  }, [userId, selectedMonth.month]);

  useEffect(() => {
    if (userId) {
      fetchEmployeeDetails();
    }
    // eslint-disable-next-line
  }, [userId]);

  const fetchUserAttendance = async () => {
    setLoading(true);
    try {

      const [response, summaryResponse] = await Promise.all([
        getUserAttendanceByUserId(
          selectedMonth.month,
          selectedMonth.year,
          userId,
        ),
        getUserAttendanceSummary(
          selectedMonth.month,
          selectedMonth.year,
          userId,
        ),
      ]);
     
      setAttendanceDetails(response?.data?.list);
      setAttendanceSummary(summaryResponse?.data ?? null);
    } catch (err) {
      console.log("error->", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async () => {
    const response = await getEmployeeDetails(userId);
    if (response?.success) {
      setEmployee(response?.data?.user);
    } else {
      setEmployee(initialState);
    }
  };

  // handle open close summary modal
  const handleSummaryOpenClose = () => {
    setOverview((prev) => !prev);
  }

  return (
    <>
      {/* Month Picker */}
      <div
        className={`flex ${id ? "justify-end" : ""} flex-col gap-2 sm:flex-row items-center border-b border-slate-200 bg-white px-4 py-1.5`}
      >
        {!id && (
          <div className="flex w-full gap-3">
            <PersonInfo
              personInfo={{
                profileImage: employee.profileImage,
                firstName: employee.firstName,
                lastName: employee.lastName,
                description: `${employee.userId} | ${roleNames[employee.role]}`,
              }}
              className="max-w-[200px] border-r pr-3 border-inputBorder"
              imageClassName="rounded-0 w-[50px] h-[50px]"
              personClassName="text-black !text-md !font-[500]"
            />
            <Tabs active={active} options={options} onChange={setActive} />
          </div>
        )}
        <div className="flex w-full gap-3 justify-end">
          {(selectedMonth.month !== new Date().getMonth() + 1 && (attendanceSummary?.attendance && attendanceSummary?.attendance?.length > 0)) && (<Button
            name="Overview"
            size="sm"
            onClick={handleSummaryOpenClose}
            leftIcon={<i className="fa-solid fa-list-check"></i>}
          />)}
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} pickerClassName="right-0" className="w-28" maxDate={{ month: new Date().getMonth() + 2, year: new Date().getFullYear() }}/>
          {!id && (
            <Button
              size="sm"
              variant={"danger"}
              onClick={() => navigate(pathNames.ATTENDANCE)}
              leftIcon={<i className="fa-solid fa-xmark fa-xl text-danger"></i>}
            />
          )}
        </div>
      </div>

      <div className="content-area">
        <PageLoader loading={loading} />

        {/* Calendar */}
        {
          {
            [AttendanceViewEnum.CALENDER_VIEW]: (
              <AttendanceCalendar
                selectedMonth={selectedMonth}
                attendanceData={attendanceDetails}
                onDateClick={(date) => {
                  console.log("Selected date:", date);
                }}
              />
            ),
            [AttendanceViewEnum.TABLE_VIEW]: (
              <EmployeeAttendanceTable
                attendance={attendanceDetails}
                refreshData={() => fetchUserAttendance()}
              />
            ),
          }[active]
        }
      </div>
      <Modal isOpen={overview} title={`${employee.firstName} ${employee.lastName} | ${employee.userId}`} onClose={handleSummaryOpenClose}>
        <AttendanceSummary data={attendanceSummary} />
      </Modal>
    </>
  );
};

export default EmployeeAttendanceDetails;
